import React from 'react';
import useAuthContext from '../../hooks/useAuthContext';
import { Title, Loader, ReloadIconButton, TablePaginationWrapper } from '../../elements';
import { useConfirmation } from '../../hooks/ConfirmationService';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import { notifications, extractApiErrorMessage } from '../../utils';
import {
  Grid,
  Button,
  Icon,
  Chip,
  Paper,
  TableRow,
  TableBody,
  TableCell,
  Table,
  Tooltip,
  IconButton,
  TableHead
} from '@material-ui/core';
import { useStyles } from './styles';
import { useTranslation } from 'react-i18next';
import { TasksService } from '../../hooks';
import { TaskListRuleViewModel } from '../../models/tasks';
import { AnimalType } from '../../models/animal';
import { AddTaskListRuleModal } from '../../components';
import UserRole from '../../constants/user-role';

const TaskListRules: React.FC<any> = () => {
  const classes = useStyles();
  const tasksService = new TasksService();
  const { state } = useAuthContext();
  const { t } = useTranslation();
  const confirmDialog = useConfirmation();

  const [rules, setRules] = React.useState([] as TaskListRuleViewModel[]);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const [addTaskListRuleModalOpened, setAddTaskListRuleModalOpened] = React.useState(false);

  const loadTaskListRules = async () => {
    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;

    tasksService
      .getTaskListRules(searchPage, rowsPerPage)
      .then((result) => {
        setRules(result.data);
        setTotal(result.total);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (loaded === false) {
      const loadData = async () => loadTaskListRules();
      loadData();
    }
  }, [loaded]);

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };
  // * pagination

  const renderAnimalTypeCell = (rule: TaskListRuleViewModel) => {
    return (
      <>
        {rule.isPregnant && (
          <Tooltip arrow title={t('f-animal-preg-only')}>
            <IconButton size="small" color="default">
              <Icon>{'eco'}</Icon>
            </IconButton>
          </Tooltip>
        )}

        {rule.isLactating && (
          <Tooltip arrow title={t('f-animal-lact-only')}>
            <IconButton size="small" color="default">
              <Icon>{'grain'}</Icon>
            </IconButton>
          </Tooltip>
        )}

        {AnimalType[rule.animalType]}
      </>
    );
  };

  const handleRemoveTaskListRule = (rule: TaskListRuleViewModel) => {
    const ruleText = `${AnimalType[rule.animalType]} from ${rule.ageFrom} to ${rule.ageTo}`;

    confirmDialog({
      dialogId: 'delete-rule-dialog',
      title: t('f-tasks-rules-del-title'),
      message: t('f-tasks-rules-del-msg', { rule: ruleText }),
      alertSeverity: 'warning'
    }).then(() => {
      tasksService
        .removeTaskListRule(rule.id)
        .then(() => {
          notifications.success(t('f-tasks-rules-del-ok'));
          setLoaded(false);
        })
        .catch((e: unknown) => {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        });
    });
  };

  const handleCreateTaskListRuleClose = (result: boolean) => {
    if (result) {
      setLoaded(false);
    }
    setAddTaskListRuleModalOpened(false);
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Grid container direction="row" className={classes.paperHeader}>
            <Grid item container md={6} xs={10} spacing={1}>
              <Grid item>
                <Icon fontSize="large" color="primary">
                  {'fact_check'}
                </Icon>
              </Grid>
              <Grid item>
                <Title>{t('f-tasks-rules')}</Title>
              </Grid>
              <Grid item>
                <Chip label={total} variant="outlined" />
              </Grid>
            </Grid>

            <Grid item container className={classes.justifyRight} md={6} xs={10}>
              <Grid item>
                <ReloadIconButton action={() => setLoaded(false)} />
              </Grid>

              <Grid item>
                <Button
                  disabled={state.role === UserRole.user}
                  id="addTaskRuleBtn"
                  size="small"
                  className={classes.withMarginLeft}
                  color="default"
                  variant="contained"
                  startIcon={<Icon>{'add_circle_outline'}</Icon>}
                  onClick={() => setAddTaskListRuleModalOpened(true)}
                >
                  {t('f-tasks-rules-add-bnt')}
                </Button>
              </Grid>
            </Grid>
          </Grid>

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {/* data table */}
          {loaded && (
            <Grid container spacing={3}>
              <Grid item md={12}>
                <TablePaginationWrapper
                  rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                  count={total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                >
                  <Paper elevation={1}>
                    <Table size="small" className={classes.diseasesTable}>
                      <TableHead className={classes.tableHeader}>
                        <TableRow>
                          <TableCell align="right">{t('f-com-animal-type')}</TableCell>
                          <TableCell align="right">{t('f-tasks-age-from')}</TableCell>
                          <TableCell>{t('f-tasks-age-to')}</TableCell>
                          <TableCell align="right">{t('f-com-supply')}</TableCell>
                          <TableCell>{t('f-com-dose')}</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {rules.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell align="right">{renderAnimalTypeCell(row)}</TableCell>
                            <TableCell align="right">{row.ageFrom}</TableCell>
                            <TableCell>{row.ageTo}</TableCell>
                            <TableCell align="right">{row.medicalSupply.number}</TableCell>
                            <TableCell>{row.dose.toFixed(2) || '-'}</TableCell>
                            <TableCell>
                              <Tooltip arrow title={t('f-com-remove')}>
                                <IconButton color="primary" size="small" onClick={() => handleRemoveTaskListRule(row)}>
                                  <Icon>{'delete_outline'}</Icon>
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </TablePaginationWrapper>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>

      <AddTaskListRuleModal opened={addTaskListRuleModalOpened} onModalAction={handleCreateTaskListRuleClose} />
    </>
  );
};

export default TaskListRules;
